import React from 'react';
import { Pie, PieChart, Sector, Tooltip, type PieLabelRenderProps, type PieSectorShapeProps } from 'recharts';
import { chartColor } from '../chartTheme';
import { ChartTooltip } from './ChartTooltip';

/** One donut slice. */
export interface DonutDatum {
  key: string;
  label: string;
  count: number;
  /** Percentage printed on the slice. */
  valueLabel: string;
}

/** Props for {@link Donut}. */
export interface DonutProps {
  data: DonutDatum[];
  onSelect?: (datum: DonutDatum) => void;
}

/** Fixed size: donuts only show 3 or fewer parts. */
const SIZE = { width: 240, height: 168 };

/**
 * Simple part-to-whole donut (3 or fewer options, or a checkbox). Slices carry
 * their percentage, colour encodes the category, and every slice is a
 * keyboard-focusable mark. Zero slices draw nothing; the table lists them.
 */
export const Donut: React.FC<DonutProps> = ({ data, onSelect }) => {
  const slices = data.map((datum, index) => ({ ...datum, fill: chartColor.categorical[index] })).filter(datum => datum.count > 0);
  /** Direct slice label: option and percentage. */
  const label = (props: PieLabelRenderProps) => {
    const datum = slices[props.index ?? 0];
    return (
      <text x={props.x} y={props.y} fill={chartColor.label} fontSize={12} textAnchor={props.textAnchor} dominantBaseline="central">
        {`${datum.label} ${datum.valueLabel}`}
      </text>
    );
  };
  /** A focusable sector that opens its respondents. */
  const shape = (props: PieSectorShapeProps, index: number) => {
    const datum = slices[index];
    return (
      <g
        tabIndex={0}
        role={onSelect ? 'button' : 'img'}
        aria-label={`${datum.label}: ${datum.valueLabel}`}
        className={onSelect ? 'cursor-pointer outline-none' : 'outline-none'}
        onClick={() => onSelect?.(datum)}
        onKeyDown={event => {
          if (onSelect && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            onSelect(datum);
          }
        }}
      >
        <Sector {...props} fill={datum.fill} stroke="var(--background)" strokeWidth={2} />
      </g>
    );
  };
  return (
    <div data-chart="donut" className="flex justify-center">
      <PieChart {...SIZE}>
        <Pie data={slices} dataKey="count" nameKey="label" innerRadius={40} outerRadius={60} isAnimationActive={false} label={label} labelLine={false} shape={shape} rootTabIndex={-1} />
        <Tooltip content={<ChartTooltip />} />
      </PieChart>
    </div>
  );
};
